import { MultiSelect } from '@orn/shared-web/components/Select';
import { UserIcon } from '@orn/shared-web/icons';
import * as React from 'react';
import { useListData } from 'react-stately';

import { buildChipItem, reconcileChipDelta, userToSelectItem } from './adminSelectUsersHelpers';
import { useAdminGateUsers } from './useAdminGateUsers';

import type { SelectItemType } from '@orn/shared-web/components/Select';

interface IAdminSelectUsersProps {
  /** Selected user ids — the single source of truth, owned by the parent form. */
  value: string[];
  onChange: (userIds: string[]) => void;
  label?: string;
  placeholder?: string;
  hint?: string;
}

/**
 * Multi-select user picker for building a feature gate's allow / deny
 * list. Loads every user once via `adminListUsers` (same precedent as
 * `AdminSelectUser`; client-side filtering) and renders a searchable
 * combobox with removable chips.
 *
 * Fully controlled by `value`: an effect reconciles the chip state to
 * `value` by delta (never a full re-seed) so the picker survives the
 * edit scene's post-save refetch without dropping or flickering chips.
 * Ids with no loaded user render as removable `unknown user` chips.
 */
const AdminSelectUsers = ({
  value,
  onChange,
  label,
  placeholder = 'Search by name or email…',
  hint,
}: IAdminSelectUsersProps) => {
  const { users, usersById, loading } = useAdminGateUsers();
  const candidateItems = React.useMemo(() => users.map(userToSelectItem), [users]);

  const selected = useListData<SelectItemType>({ initialItems: [] });

  // Reconcile chips to `value` by delta once users have loaded. User
  // edits flow out through `onChange`; the resulting `value` change then
  // produces an empty delta here, so there is no re-seed loop.
  React.useEffect(() => {
    if (loading) {
      return;
    }
    const { toAppend, toRemove } = reconcileChipDelta(
      selected.items.map((item) => item.id),
      value
    );
    for (const id of toAppend) {
      selected.append(buildChipItem(id, usersById));
    }
    for (const id of toRemove) {
      selected.remove(id);
    }
    // `selected` is a stable react-stately handle; depending on it would
    // not change reconcile behavior and risks an extra run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, loading, usersById]);

  if (loading) {
    return <div className="text-fg-secondary text-sm">Loading users…</div>;
  }

  // Render the label ourselves (passing `aria-label` to MultiSelect
  // instead of `label`) so the "Clear all" affordance can sit on the
  // label row, shown only when there's something to clear.
  return (
    <div className="flex flex-col gap-1.5">
      {(label || value.length > 0) && (
        <div className="flex items-center justify-between gap-2">
          {label ? <span className="text-secondary text-sm font-bold">{label}</span> : <span />}
          {value.length > 0 && (
            // Deliberately a raw <button>, not the mops-web Button component
            // — this reads as an inline text affordance on the label row, not
            // a CTA, and Button's color/size/border variants would fight that
            // aesthetic (same rationale as the "Advanced" disclosure toggle in
            // FeatureGateForm). The focus ring is wired explicitly.
            <button
              type="button"
              onClick={() => onChange([])}
              aria-label={label ? `Clear ${label}` : 'Clear all'}
              className="text-fg-tertiary hover:text-fg-primary focus-visible:outline-border-brand text-xs uppercase tracking-wider focus-visible:outline-2 focus-visible:-outline-offset-1"
            >
              Clear all
            </button>
          )}
        </div>
      )}
      <MultiSelect
        aria-label={label}
        placeholder={placeholder}
        hint={hint}
        shortcut={false}
        items={candidateItems}
        selectedItems={selected}
        onItemInserted={(key) => {
          const id = String(key);
          if (!value.includes(id)) {
            onChange([...value, id]);
          }
        }}
        onItemCleared={(key) => onChange(value.filter((id) => id !== String(key)))}
      >
        {(item) => (
          <MultiSelect.Item id={item.id} supportingText={item.supportingText} icon={UserIcon}>
            {item.label}
          </MultiSelect.Item>
        )}
      </MultiSelect>
    </div>
  );
};

export default AdminSelectUsers;
